import { useEffect, useRef, useState } from 'react';
import api, { ApiError } from '../api/client.js';
import { MEETING_TYPES, MEETING_TYPE_LABELS } from '../lib/constants.js';

const EMPTY = {
  date: '',
  time: '',
  title: '',
  type: 'رسمي',
  location: '',
  notes: '',
};

/**
 * One modal serves both adding and editing, keyed on whether `meeting` is null.
 * Two near-identical forms would drift apart over time.
 */
export default function MeetingModal({ meeting, defaultDate, onClose, onSaved }) {
  const isEditing = Boolean(meeting);

  const [form, setForm] = useState(() =>
    meeting
      ? {
          date: meeting.date,
          time: meeting.time,
          title: meeting.title,
          type: meeting.type,
          location: meeting.location || '',
          notes: meeting.notes || '',
        }
      : { ...EMPTY, date: defaultDate }
  );

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    // Guard against a double tap producing two rows — the exact duplicate-entry
    // problem the original system had to defend against.
    if (submitting) return;

    setFormError('');
    setErrors({});
    setSubmitting(true);

    const payload = {
      date: form.date,
      time: form.time,
      title: form.title.trim(),
      type: form.type,
      location: form.location.trim(),
      notes: form.notes.trim(),
    };

    try {
      if (isEditing) {
        // Preserve the existing status; editing a completed appointment's time
        // must not silently reopen it.
        await api.updateMeeting(meeting.id, { ...payload, status: meeting.status });
      } else {
        await api.createMeeting(payload);
      }
      onSaved(isEditing ? 'تم تحديث الموعد' : 'تمت إضافة الموعد');
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFormError(caught.message);
        if (caught.details) setErrors(caught.details);
      } else {
        setFormError('تعذّر حفظ الموعد');
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(event) => {
        // Close on a click landing on the backdrop itself, not on a drag that
        // started inside the dialog and ended outside it.
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="meeting-modal-title"
        ref={dialogRef}
      >
        <div className="modal-header">
          <h2 id="meeting-modal-title">{isEditing ? 'تعديل الموعد' : 'إضافة موعد جديد'}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="إغلاق"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            {formError && (
              <div className="form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="field-row">
              <div className="field">
                <label htmlFor="meeting-date">
                  التاريخ <span className="required">*</span>
                </label>
                <input
                  id="meeting-date"
                  type="date"
                  value={form.date}
                  onChange={(event) => update('date', event.target.value)}
                  aria-invalid={Boolean(errors.date)}
                  ref={firstFieldRef}
                  required
                />
                {errors.date && <div className="field-error">{errors.date}</div>}
              </div>

              <div className="field">
                <label htmlFor="meeting-time">
                  الوقت <span className="required">*</span>
                </label>
                <input
                  id="meeting-time"
                  type="time"
                  value={form.time}
                  onChange={(event) => update('time', event.target.value)}
                  aria-invalid={Boolean(errors.time)}
                  required
                />
                {errors.time && <div className="field-error">{errors.time}</div>}
              </div>
            </div>

            <div className="field">
              <label htmlFor="meeting-title">
                عنوان الموعد <span className="required">*</span>
              </label>
              <input
                id="meeting-title"
                type="text"
                value={form.title}
                onChange={(event) => update('title', event.target.value)}
                maxLength={200}
                aria-invalid={Boolean(errors.title)}
                placeholder="مثال: اجتماع مع مديري المؤسسات"
                required
              />
              {errors.title && <div className="field-error">{errors.title}</div>}
            </div>

            <div className="field-row">
              <div className="field">
                <label htmlFor="meeting-type">نوع الموعد</label>
                <select
                  id="meeting-type"
                  value={form.type}
                  onChange={(event) => update('type', event.target.value)}
                  aria-invalid={Boolean(errors.type)}
                >
                  {MEETING_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {MEETING_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
                {errors.type && <div className="field-error">{errors.type}</div>}
              </div>

              <div className="field">
                <label htmlFor="meeting-location">المكان</label>
                <input
                  id="meeting-location"
                  type="text"
                  value={form.location}
                  onChange={(event) => update('location', event.target.value)}
                  maxLength={200}
                  placeholder="مثال: قاعة الاجتماعات"
                />
                {errors.location && <div className="field-error">{errors.location}</div>}
              </div>
            </div>

            <div className="field">
              <label htmlFor="meeting-notes">ملاحظات</label>
              <textarea
                id="meeting-notes"
                value={form.notes}
                onChange={(event) => update('notes', event.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="أي تفاصيل إضافية"
              />
              {errors.notes && <div className="field-error">{errors.notes}</div>}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={submitting}
            >
              إلغاء
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '...جاري الحفظ' : 'حفظ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
